import React, { useState, useEffect, useRef } from 'react';
import { apiService, isAdmin } from '../services/api';
import { downloadTransactionsCsv } from '../utils/transactionsExport';
import { ModelLines } from './ModelLines';
import IssuesBar from './IssuesBar';
import ProcessingStatus from './ProcessingStatus';
import { Skel } from './Skeleton';
import BusyIcon from './BusyIcon';
import { CheckCircle2, Clock, XCircle, RefreshCw, AlertCircle, Timer, ShieldCheck, Calendar, Activity, Lock, Unlock, Download, BellOff, ChevronRight, ChevronDown } from 'lucide-react';

// Model-version chip colors — kept in sync with Reports.js (distinct hues, forward-only).
const VERSION_CHIP = {
  1: 'bg-slate-600/40 text-slate-200 border-slate-500/50',
  2: 'bg-indigo-500/25 text-indigo-200 border-indigo-400/50',
  3: 'bg-amber-500/25 text-amber-200 border-amber-400/50',
  4: 'bg-emerald-500/25 text-emerald-200 border-emerald-400/50',
  5: 'bg-rose-500/25 text-rose-200 border-rose-400/50',
};

// ─── Data sources health strip ─────────────────────────────────────────────────
// One question, answered definitively: "can we connect to each of our data sources
// and get CORRECT data right now?" Every chip is an active self-test — the backend
// fetches a fixed settled day (14-06-2024) from the source and compares it to the
// known official close. Works at any time of day; market hours are irrelevant.
const SOURCE_STATE_STYLE = {
  ok: { dot: 'bg-green-400', text: 'text-dark-300', border: 'border-dark-600' },
  wrong_value: { dot: 'bg-amber-400', text: 'text-amber-300', border: 'border-amber-500/40' },
  down: { dot: 'bg-red-400', text: 'text-red-300', border: 'border-red-500/40' },
  checking: { dot: 'bg-dark-500', text: 'text-dark-400', border: 'border-dark-600' },
};

// the markets and indexes seen last visit — the first-load skeleton's rows
const LAYOUT_KEY = 'todayHealth.layout.v1';

const CONNECTOR_ORDER = ['EODHD', 'YAHOO', 'NSE', 'BSE', 'MOEX', 'COINBASE'];
const CONNECTOR_LABELS = { EODHD: 'EODHD', YAHOO: 'Yahoo', NSE: 'NSE', BSE: 'BSE', MOEX: 'MOEX', COINBASE: 'Coinbase' };
const CONNECTOR_TEST_DESC = {
  EODHD: 'Self-test: NIFTY50 close for 14-06-2024, expected 23,465.60',
  YAHOO: 'Self-test: NIFTY50 close for 14-06-2024, expected 23,465.60',
  NSE: 'Self-test via India proxy: NIFTY50 close for 14-06-2024, expected 23,465.60',
  BSE: 'Self-test via India proxy: SENSEX close for 14-06-2024, expected 76,992.77',
  MOEX: 'Self-test: IMOEX candle for 14-06-2024 (data returned = connected)',
  COINBASE: 'Self-test: BTC-USD daily candle for 14-06-2024 (data returned = connected)',
};

// For a symbol a down source verified: which OK sources still cover it?
export const coveringSources = (symbol, connectors, order = CONNECTOR_ORDER) =>
  order.filter(k => connectors[k]?.status === 'ok' && (connectors[k]?.covers || []).includes(symbol));

// One brief, risk-first line per not-ok source. Blind symbols (no other OK
// source) are the headline; full redundancy is reassurance, not an alarm.
export const impactLine = (key, imp, connectors) => {
  const label = CONNECTOR_LABELS[key] || key;
  if (imp.blind && imp.blind.length > 0) {
    return { key, severity: 'blind', text: `${label} down — ${imp.blind.join(', ')} unverified until a source recovers` };
  }
  if (imp.symbols.length === 1) {
    const sym = imp.symbols[0];
    const via = coveringSources(sym, connectors).map(k => CONNECTOR_LABELS[k] || k).join(' + ');
    return { key, severity: 'covered', text: `${label} down — ${sym} still verified via ${via || 'other sources'}` };
  }
  return { key, severity: 'covered', text: `${label} down — all ${imp.symbols.length} indexes it verifies are still covered by other sources` };
};

// Classify WHY a 'pending' validation has no source values. Three very
// different situations that must never share a label (the FTSE lesson —
// "sources down" while every connector was green):
//   'No ticker'                          -> N/A, source doesn't cover the symbol
//   'No data returned' / 'No close data' -> source answered fine, it just hasn't
//     / 'No candle data'                    PUBLISHED this session's close yet
//   anything else (timeout, reset, HTTP) -> the source is actually unreachable
const NOT_PUBLISHED = /^no (data returned|close data|candle data)/i;
export const sourcesDownInfo = (result) => {
  if (!result || result.status !== 'pending') return null;
  const entries = Object.entries(result.sources || {})
    .filter(([, s]) => s && s.close == null && s.error && s.error !== 'No ticker');
  if (entries.length === 0) return null;
  const named = entries.map(([name, s]) => ({ name: name.toUpperCase(), error: s.error }));
  return {
    down: named.filter(e => !NOT_PUBLISHED.test(e.error)),
    waiting: named.filter(e => NOT_PUBLISHED.test(e.error)),
  };
};

// One severity per Today's-Health row. Apal's attention list is exactly
// three things — anything else stays quiet (its own inline chip is enough):
//   1. close MISMATCH (settled close disagrees with sources)
//   2. data NOT UPDATING (missing session / failed update with data missing)
//   3. model update breaking non-negotiables (material risk change)
export const rowSeverity = (item, validationResult, diff, reviewedByMe = false) => {
  const mismatch = validationResult?.status === 'failed' && !validationResult?.settling;
  if (item.data_current === false || mismatch
      || (item.status === 'failed' && item.data_current !== true)
      || (diff?.impact?.material && !reviewedByMe)) {
    return 'red';
  }
  // a model warning THIS user dismissed: grey, not red — never hidden
  if (diff?.impact?.material && reviewedByMe) return 'reviewed';
  return null;
};

// This user's dismissal of a symbol's CURRENT model-change warning, and who
// else dismissed it. Matched on the fingerprint (model version + failing checks):
// a new model or a different failing check is a new issue, red again for all.
export const reviewOf = (reviews, symbol, diff) => {
  const fp = diff?.impact?.material ? diff.impact.fingerprint : null;
  if (!fp) return { mine: null, others: [] };
  const rows = (reviews || []).filter(r => r.symbol === symbol && r.fingerprint === fp);
  return {
    mine: rows.find(r => r.mine && r.action === 'dismissed') || null,
    others: rows.filter(r => !r.mine && r.action === 'dismissed'),
  };
};

const reviewDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '');

// Plain-words reason for a red row — the issues bar names WHY, not just who.
// Order mirrors rowSeverity so bar and row can never disagree.
export const redReason = (item, validationResult, diff, reviewedByMe = false) => {
  if (validationResult?.status === 'failed' && !validationResult?.settling) return 'close mismatch';
  if (item.data_current === false) return 'data missing';
  if (item.status === 'failed' && item.data_current !== true) return 'update failing';
  if (diff?.impact?.material && !reviewedByMe) return 'model risk change';
  return null;
};

const DataSourcesStrip = ({ health, testing, onTest, failed = false }) => {
  const connectors = health?.connectors || {};
  const checkedAt = health?.checked_at ? new Date(health.checked_at * 1000) : null;
  const impact = health?.verification_impact || {};
  const impactLines = Object.entries(impact).map(([key, imp]) => impactLine(key, imp, connectors));

  const tiles = CONNECTOR_ORDER.map(key => {
    const c = connectors[key];
    if (!c) {
      return failed
        ? { key, label: CONNECTOR_LABELS[key], state: 'checking', word: 'no answer', detail: `The self-test could not be reached — Test now to retry.\n${CONNECTOR_TEST_DESC[key]}` }
        : { key, label: CONNECTOR_LABELS[key], state: 'checking', word: null, detail: CONNECTOR_TEST_DESC[key] };
    }
    const covers = c.covers && c.covers.length ? `\nVerifies: ${c.covers.join(', ')}` : '';
    const base = `${CONNECTOR_TEST_DESC[key]}${covers}\n${c.latency_ms != null ? `Response: ${c.latency_ms}ms` : ''}${checkedAt ? `\nChecked: ${checkedAt.toLocaleTimeString('en-GB', { timeZone: 'Asia/Kolkata' })} IST` : ''}`;
    if (c.status === 'ok') return { key, label: CONNECTOR_LABELS[key], state: 'ok', word: 'working', detail: base };
    if (c.status === 'wrong_value') return { key, label: CONNECTOR_LABELS[key], state: 'wrong_value', word: 'wrong data', detail: `Connected but ${c.detail}.\n${base}` };
    return { key, label: CONNECTOR_LABELS[key], state: 'down', word: 'down', detail: `Cannot fetch: ${c.detail || 'no response'}.\n${base}` };
  });

  return (
    <div className="bg-dark-700/30 rounded-lg p-4 border border-dark-600/20">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider">Data Sources</h3>
        <button onClick={onTest} disabled={testing} aria-busy={testing} data-testid="sources-test"
          title="Re-run the connector self-tests now"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-dark-600 text-xs text-dark-300 hover:text-white hover:bg-dark-700 transition-colors disabled:opacity-50">
          <BusyIcon busy={testing} icon={RefreshCw} className="w-[11px] h-[11px]" />
          {testing ? 'Testing…' : 'Test now'}
        </button>
      </div>
      <div className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(110px,1fr))]">
        {tiles.map(t => {
          const st = SOURCE_STATE_STYLE[t.state];
          return (
            <div key={t.key} title={t.detail}
              className={`px-3 py-1.5 rounded-lg bg-dark-800/60 border cursor-help ${st.border}`}>
              <div className="text-xs font-semibold text-white">{t.label}</div>
              {t.word == null ? (
                // its self-test is still running: the source's name stays, its answer waits
                <div className="flex items-center h-[16.5px]" data-testid={`source-pending-${t.key}`}><Skel className="h-2.5 w-14" /></div>
              ) : (
                <div className={`flex items-center gap-1.5 text-[11px] ${st.text} cb-fade-in`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                  {t.word}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {impactLines.length > 0 && (
        <div className="mt-2.5 space-y-0.5">
          {impactLines.map(l => (
            // Blind = red alarm. Covered = quiet grey with a green check — a
            // working backup is reassurance, never a warning color.
            <div key={l.key} className={`text-[11px] ${l.severity === 'blind' ? 'text-red-400 font-semibold' : 'text-dark-300'}`}>
              {l.severity === 'blind' ? '⚠ ' : <span className="text-emerald-400">✓ </span>}{l.text}
            </div>
          ))}
          <div className="text-[10px] text-dark-500">
            Sources only verify closes — market data flows from MarketVibes regardless.
          </div>
        </div>
      )}
    </div>
  );
};

// One popup for a model warning (Apal 2026-10-29): Dismiss writes a required
// comment; the grey "Dismissed" indicator opens the same popup to read it and
// "Bring warning back". Other users' dismissals show here, never applied.
// The six figures a released model is judged on, as one aligned scorecard:
// fixed columns, Published and Latest rows, each Latest coloured by the
// server's call. Same on the dashboard row and in the popup.
// a ratio carries no unit; every other figure is a %
const unitOf = (m) => (m.key === 'exp_ratio' ? '' : '%');
const RiskScorecard = ({ metrics, version, muted = false }) => (
  <>
    <span />
    {metrics.map(m => (
      <span key={m.key} className={`text-[10px] leading-tight self-end ${m.call === 'worse' && !muted ? 'text-red-300/80' : 'text-dark-500'}`}>
        {m.label.charAt(0).toUpperCase() + m.label.slice(1)}
      </span>
    ))}
    <span className="text-[10px] text-dark-500 whitespace-nowrap">Published</span>
    {metrics.map(m => (
      <span key={m.key} className="whitespace-nowrap text-dark-400">{m.published}{unitOf(m)}</span>
    ))}
    <span className="text-[10px] text-dark-500 whitespace-nowrap">Latest{version != null ? ` v${version}` : ''}</span>
    {metrics.map(m => (
      <span key={m.key} data-call={m.call}
        className={`whitespace-nowrap ${m.call === 'worse' ? (muted ? 'text-red-400/60' : 'text-red-400 font-semibold') : m.call === 'better' ? 'text-emerald-400' : 'text-dark-300'}`}>
        {m.latest}{unitOf(m)}
      </span>
    ))}
  </>
);
// columns sized to their figures and spread across the row; a header wraps
// onto two lines only when the row is too narrow for six on one line
const SCORECARD_COLS = 'grid grid-cols-[auto_repeat(6,auto)] justify-between gap-x-1 gap-y-0.5 text-left tabular-nums';
const SCORECARD_GRID = `${SCORECARD_COLS} rounded-md border px-2.5 py-1.5`;

const NO_DIFF = {};

const DismissPopup = ({ popup, review, onChange, onClose, onDismiss, onRestore }) => {
  if (!popup) return null;
  const mine = review?.mine;
  const others = review?.others || [];
  const ok = (popup.note || '').trim().length >= 3;
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-dark-800 border border-dark-600 rounded-xl p-5 w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}
        data-testid="dismiss-popup">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-sm font-semibold text-white">{popup.symbol} · model risk change</div>
            {/* every figure this dismissal covers, and what the two numbers are */}
            {!popup.metrics?.length && (
              <ul className="mt-1 space-y-0.5" data-testid="dismiss-risks">
                {(popup.risks || []).map(r => (
                  <li key={r} className="text-xs text-red-400 font-semibold">⚠ {r.charAt(0).toUpperCase() + r.slice(1)}</li>
                ))}
              </ul>
            )}
            <div className="text-[11px] text-dark-400 mt-1">Published (live, what clients see) → Latest{popup.version != null ? ` model v${popup.version}` : ''}</div>
          </div>
          <button onClick={onClose} className="text-dark-400 hover:text-white text-sm px-1" aria-label="Close">✕</button>
        </div>
        {/* the six figures a released model is judged on (Apal 2026-10-30: "Cagr, win
            rate, worst loss, streak, avg return"; 2026-11-03: + expectancy ratio), each coloured by the server's call —
            here, not on the dashboard row, which keeps one line ("I don't like that clutter") */}
        {popup.metrics?.length > 0 && (
          <div data-testid="dismiss-figures" className={`${SCORECARD_GRID} mb-4 text-xs ${mine && popup.mode === 'view' ? 'border-dark-700 bg-dark-900/30' : 'border-red-500/25 bg-red-500/[0.06]'}`}>
            <RiskScorecard metrics={popup.metrics} version={popup.version} muted={!!(mine && popup.mode === 'view')} />
          </div>
        )}
        {mine && popup.mode === 'view' ? (
          <div className="space-y-3">
            <div className="rounded-lg bg-dark-700/50 border border-dark-600 px-3 py-2" data-testid="dismiss-mine">
              <div className="text-[11px] text-dark-400 mb-1">Dismissed by you · {reviewDate(mine.created_at)}</div>
              <div className="text-sm text-dark-100 whitespace-pre-wrap break-words">{mine.note}</div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-1.5 rounded text-sm text-dark-300 hover:text-white">Close</button>
              <button onClick={onRestore} data-testid="restore-warning" disabled={!!popup.saving} aria-busy={!!popup.saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-semibold bg-red-500/15 text-red-300 border border-red-400/40 hover:bg-red-500/25 hover:text-white disabled:opacity-60">
                <BusyIcon busy={!!popup.saving} className="w-3.5 h-3.5" />
                {popup.saving ? 'Bringing back…' : 'Bring warning back'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block text-xs text-dark-400" htmlFor="dismiss-note">Why is it OK to dismiss? <span className="text-dark-500">(required)</span></label>
            <textarea id="dismiss-note" autoFocus rows={3} maxLength={500} value={popup.note || ''}
              onChange={e => onChange(e.target.value)} data-testid="dismiss-note"
              placeholder="e.g. 273% was tested on v5 — acceptable"
              className="w-full px-3 py-2 rounded-lg bg-dark-900 border border-dark-600 text-sm text-dark-100 placeholder-dark-500 focus:outline-none focus:border-primary-500" />
            {popup.error && <div className="text-xs text-red-400">{popup.error}</div>}
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-dark-500">Hidden only for you. It comes back with a new model, or if another figure gets worse.</span>
              <div className="flex gap-2 shrink-0">
                <button onClick={onClose} className="px-3 py-1.5 rounded text-sm text-dark-300 hover:text-white">Cancel</button>
                <button onClick={onDismiss} disabled={!ok || !!popup.saving} data-testid="dismiss-save" aria-busy={!!popup.saving}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-semibold bg-primary-600 text-white disabled:opacity-40">
                  <BusyIcon busy={!!popup.saving} className="w-3.5 h-3.5" />
                  {popup.saving ? 'Saving…' : 'Dismiss warning'}
                </button>
              </div>
            </div>
          </div>
        )}
        {others.length > 0 && (
          <div className="mt-4 pt-3 border-t border-dark-700 space-y-2" data-testid="dismiss-others">
            <div className="text-[11px] uppercase tracking-wider text-dark-500">Also dismissed by</div>
            {others.map(o => (
              <div key={o.username} className="text-xs">
                <span className="text-dark-200 font-semibold">{o.username}</span>
                <span className="text-dark-500"> · {reviewDate(o.created_at)}</span>
                <div className="text-dark-300 whitespace-pre-wrap break-words">{o.note}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// No layout remembered yet (first visit): a generic shape.
const DEFAULT_LAYOUT = [['india_stocks_indexes', [null, null, null]], ['us_stocks_indexes', [null, null, null]]];

// An index row still waiting for its status: the name (remembered from the
// last visit) stays; every figure is a shimmer in its own place.
const RowSkeleton = ({ symbol }) => (
  <div className="p-3 rounded-lg bg-dark-800/50" data-testid={`row-skeleton-${symbol || 'x'}`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 h-5">
        <Skel className="h-4 w-4" rounded="rounded-full" />
        {symbol ? <span className="text-white font-medium text-sm">{symbol}</span> : <Skel className="h-3.5 w-20" />}
        <Skel className="h-[18px] w-7" />
        <Skel className="h-3.5 w-20" />
      </div>
      <Skel className="h-3 w-28" />
    </div>
    <div className="flex items-center gap-3 mt-1.5 ml-6 h-[22px]">
      <Skel className="h-2.5 w-28" />
      <Skel className="h-[18px] w-14" />
      <Skel className="h-[18px] w-32" rounded="rounded-full" />
    </div>
    <div className="mt-1.5 ml-6 space-y-0.5">
      {[0, 1].map(i => (
        <div key={i} className="flex items-center gap-3 h-[16.5px]"><Skel className="h-2.5 w-16" /><Skel className="h-2.5 w-44" /></div>
      ))}
    </div>
  </div>
);

// A delivery tile still waiting: two lines in the tile's own box and size.
const TileSkeleton = () => (
  <div className="px-3 py-1.5 rounded-lg bg-dark-800/60 border border-dark-600">
    <div className="h-4 flex items-center"><Skel className="h-2.5 w-12" /></div>
    <div className="h-[16.5px] flex items-center"><Skel className="h-2 w-16" /></div>
  </div>
);

// A summary card still waiting for its answer: the card's subject in words,
// its figure as a shimmer — the same height as the real card.
const KpiSkeleton = ({ label, testId }) => (
  <div className="rounded-lg p-4 border bg-dark-700/50 border-dark-600/30" data-testid={testId}>
    <div className="h-8 flex items-center"><Skel className="h-6 w-28" /></div>
    <div className="text-xs mt-1 text-dark-400">{label} · checking…</div>
  </div>
);

const PollingStatusMonitor = () => {
  const [pollingData, setPollingData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationData, setValidationData] = useState(null);
  const [validationLoading, setValidationLoading] = useState(true);
  const [justRefreshed, setJustRefreshed] = useState(false);
  const [lockStatus, setLockStatus] = useState({});
  const [modelDiff, setModelDiff] = useState({});
  const [indiaVerify, setIndiaVerify] = useState({});
  const [connectorHealth, setConnectorHealth] = useState(null);
  const [connectorTesting, setConnectorTesting] = useState(false);
  const [connectorFailed, setConnectorFailed] = useState(false);
  const [lockLoaded, setLockLoaded] = useState(false);
  const [validationFailed, setValidationFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // the markets and indexes seen last time, so the first-load skeleton has
  // the real rows (per-browser convenience; the page works without it)
  const [knownLayout] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LAYOUT_KEY) || 'null'); } catch { return null; }
  });
  const [todayPlans, setTodayPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [diffLoaded, setDiffLoaded] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoaded, setReviewsLoaded] = useState(false);
  const [dismissPopup, setDismissPopup] = useState(null);
  // a risk change YOU dismissed folds to one line; these are the ones opened
  // again for a look (Apal 2026-11-02: "hide it … users can always expand it")
  const [openedDismissed, setOpenedDismissed] = useState(() => new Set());
  const toggleDismissed = (sym) => setOpenedDismissed(prev => {
    const n = new Set(prev);
    if (n.has(sym)) n.delete(sym); else n.add(sym);
    return n;
  });  // { symbol, fingerprint, risk, version, mode: 'dismiss'|'view', note, error }
  // Issues bar: clicked chip scrolls to its element and flashes it briefly
  // so the eye lands on the right row, not just the right region.
  const [flashKey, setFlashKey] = useState(null);
  const flashTimer = useRef(null);
  const isMounted = useRef(true);

  // A model verdict is shown only once BOTH the diff and your dismissals are in —
  // otherwise a dismissed warning flashes red first and then flips (Apal
  // 2026-10-30: "for slow servers this is gonna be a problem, so make it clean").
  const diffReady = diffLoaded && reviewsLoaded;
  const shownDiff = diffReady ? modelDiff : NO_DIFF;

  const jumpTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlashKey(id);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => { if (isMounted.current) setFlashKey(null); }, 2600);
  };

  // Today's action-plan delivery status per market — the client-facing proof
  // strip ("was my plan sent?"). Light call; server filters by permissions.
  const fetchTodayPlans = async () => {
    try {
      const res = await apiService.getNotificationHistory({ days: 1 });
      if (isMounted.current) setTodayPlans(res.data?.today_plans || []);
    } catch (e) { /* strip simply hides on error */
    } finally {
      if (isMounted.current) setPlansLoading(false);
    }
  };

  // Active connector self-tests (admin-only strip). force=true re-runs the checks
  // server-side instead of serving the 10-minute cache.
  const fetchConnectorHealth = async (force = false) => {
    if (!isAdmin()) return;
    try {
      if (force) {
        setConnectorTesting(true);
        setConnectorHealth(null); // chips drop to "Checking…" so the re-run is visible
      }
      const res = await apiService.getConnectorHealth(force ? { force: 1 } : {});
      if (isMounted.current) { setConnectorHealth(res.data); setConnectorFailed(false); }
    } catch (e) {
      console.error('Connector health failed', e);
      if (isMounted.current) setConnectorFailed(true);
    } finally {
      if (isMounted.current) setConnectorTesting(false);
    }
  };

  // Independent NSE/BSE close verification for India indexes (authoritative after
  // 21:30 IST). Provisional during the day; fail-safe: no badge on error.
  const fetchIndiaVerification = async () => {
    try {
      const response = await apiService.getIndiaCloseVerification();
      if (isMounted.current) setIndiaVerify(response.data?.verifications || {});
    } catch {
      if (isMounted.current) setIndiaVerify({});
    }
  };

  // Live symbol lock/model status (admin -> engine -> MV). Fail-safe: if the
  // engine/MV is unreachable, we just show no lock badges — nothing breaks.
  const fetchLockStatus = async () => {
    try {
      const response = await apiService.getLockStatus();
      if (isMounted.current) setLockStatus(response.data?.symbols || {});
    } catch {
      if (isMounted.current) setLockStatus({});
    } finally {
      if (isMounted.current) setLockLoaded(true);
    }
  };

  // Published vs Latest per symbol ("did the current model change"). Cached
  // server-side per push, so this is cheap. Fail-safe: no badges on error.
  const fetchReviews = async () => {
    try {
      const res = await apiService.getIssueReviews();
      if (isMounted.current) setReviews(res.data?.reviews || []);
    } catch {
      // fail-safe: without reviews every issue simply stays red
    } finally {
      if (isMounted.current) setReviewsLoaded(true);
    }
  };

  const submitReview = async (symbol, fingerprint, action, note = '') => {
    // the popup's button says it is saving until the row underneath has its new state
    setDismissPopup(r => (r ? { ...r, saving: action, error: null } : r));
    try {
      await apiService.postIssueReview({ symbol, fingerprint, action, note });
      await fetchReviews();
      if (isMounted.current) setDismissPopup(null);
    } catch (e) {
      const msg = e?.response?.data?.error || 'Could not save — try again';
      if (isMounted.current) setDismissPopup(r => (r ? { ...r, saving: null, error: msg } : r));
    }
  };

  const fetchModelDiff = async (force = false) => {
    try {
      // On a manual Refresh, force a fresh model-diff run (bypass the push-time cache)
      // so the model-change info updates alongside the model-push line.
      const response = await apiService.getModelDiff(force ? { refresh: 1 } : {});
      if (isMounted.current) setModelDiff(response.data || {});
    } catch {
      if (isMounted.current) setModelDiff({});
    } finally {
      if (isMounted.current) setDiffLoaded(true);
    }
  };

  const fetchPollingStatus = async () => {
    try {
      if (isMounted.current) setError(null);
      const response = await apiService.getPollingStatus();
      if (isMounted.current) {
        setPollingData(response.data);
        setLoading(false);
      }
    } catch (err) {
      if (isMounted.current) {
        setError(`Failed to load polling status: ${err.response?.data?.detail || err.message}`);
        setLoading(false);
      }
    }
  };

  const fetchValidation = async (force = false) => {
    try {
      const params = force ? { force: '1' } : {};
      const response = await apiService.getClosePriceValidation(params);
      if (isMounted.current) {
        setValidationData(response.data);
        setValidationFailed(false);
        setValidationLoading(false);
      }
    } catch {
      if (isMounted.current) { setValidationFailed(true); setValidationLoading(false); }
    }
  };

  // Stale-while-revalidate: existing data stays on screen while fresh data
  // loads (nothing blanks or jumps), the button narrates its three phases
  // (Refresh -> Refreshing… -> ✓ Updated), and "Checked HH:MM" keeps the
  // freshness fact visible without anyone clicking anything.
  const handleRefresh = async () => {
    setRefreshing(true);
    setValidationLoading(true);
    await Promise.allSettled([
      fetchPollingStatus(),
      fetchValidation(true),
      fetchLockStatus(),
      fetchModelDiff(true),
      fetchIndiaVerification(),
    ]);
    if (isMounted.current) {
      setRefreshing(false);
      setJustRefreshed(true);
      setTimeout(() => { if (isMounted.current) setJustRefreshed(false); }, 2500);
    }
  };

  // Download a symbol's transactions (Published or Latest) as CSV. Uses the shared
  // exporter so the file is byte-for-byte identical to the individual-index Reports
  // export — letting users compare Published vs Latest the same way from the dashboard.
  const [downloading, setDownloading] = useState(null);   // `${symbol}:${source}` while its file is fetched
  const handleDownloadTransactions = async (symbol, source) => {
    setDownloading(`${symbol}:${source}`);
    try {
      // Request with_shorts at next-day open so the trades match the "Latest model
      // differs · N trades" badge (model-diff compares with_shorts, both books at
      // next-day open). The backend downgrades to 1x per-symbol when shorts aren't
      // permitted and echoes the effective strategy, which names the file accurately.
      const res = await apiService.getAllTransactions({ symbol, source, strategy: 'with_shorts', execution: 'next_day_open' });
      downloadTransactionsCsv(res.data, { symbolName: symbol, source, strategy: res.data?.strategy, executionMode: 'next_day_open' });
    } catch (e) {
      console.error(`Download ${source} transactions failed`, e);
    } finally {
      if (isMounted.current) setDownloading(null);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    fetchPollingStatus();
    fetchValidation();
    fetchLockStatus();
    // A page RELOAD forces a fresh diff — users hard-refresh expecting fresh
    // data and won't remember a separate Refresh button.
    const nav = performance.getEntriesByType?.('navigation')?.[0];
    fetchModelDiff(nav?.type === 'reload');
    fetchIndiaVerification();
    fetchConnectorHealth();
    fetchTodayPlans();
    fetchReviews();

    // Auto-refresh polling + lock + model diff + India NSE/BSE verdict (not the
    // aggregator close validation, which is heavier and stays manual/on-load).
    const refreshInterval = setInterval(() => { fetchPollingStatus(); fetchLockStatus(); fetchModelDiff(); fetchIndiaVerification(); fetchTodayPlans(); }, 30000);
    return () => {
      isMounted.current = false;
      clearInterval(refreshInterval);
    };
  }, []);

  // One meaningful verdict per index: green check = updates flowing AND no
  // session missing; red = data actually missing; amber = poll attempt failed
  // but nothing is missing. Hover always explains exactly what it means.
  const getStatusIcon = (item) => {
    const wrap = (title, node) => (
      <span title={title} data-testid={`status-${item.symbol}`} className="inline-flex cursor-help">{node}</span>
    );
    if (item.data_current === false) {
      return wrap(
        `Not working — data is missing: expected through ${item.expected_through} (last completed session, weekends/holidays accounted for) but the latest stored session is ${item.data_through || 'none'}.`,
        <XCircle size={16} className="text-red-400" />);
    }
    if (item.status === 'failed') {
      return wrap(
        'Last update attempt failed, but no data is missing — clears on the next successful update.',
        <AlertCircle size={16} className="text-amber-400" />);
    }
    if (item.status === 'processing') {
      return wrap('Processing…', <Clock size={16} className="text-yellow-400 animate-pulse" />);
    }
    return wrap(
      `Working — updates flowing, data complete through ${item.data_through || 'the last session'} (weekends/holidays accounted for).`,
      <CheckCircle2 size={16} className="text-green-400" />);
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-0.5 rounded-full text-[10px] font-medium inline-flex items-center gap-1";
    switch (status) {
      case 'ready':
        return `${baseClasses} bg-green-500/20 text-green-400 border border-green-500/30`;
      case 'processing':
        return `${baseClasses} bg-yellow-500/20 text-yellow-400 border border-yellow-500/30`;
      case 'failed':
        return `${baseClasses} bg-red-500/20 text-red-400 border border-red-500/30`;
      default:
        return `${baseClasses} bg-gray-500/20 text-gray-400 border border-gray-500/30`;
    }
  };

  const formatCloseTimeIST = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
  };

  const getSourceLabel = (source) => {
    if (!source) return null;
    const names = {
      'eodhd': 'EODHD',
      'yahoo': 'Yahoo',
      'nse': 'NSE',
      'bse': 'BSE',
    };
    return names[source.toLowerCase()] || source.toUpperCase();
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getMarketOpenBadge = (marketOpen, validationResult) => {
    if (marketOpen === null || marketOpen === undefined) return null;
    if (marketOpen) {
      // Live pulse ring — same visual language as the app's open-market rings
      return (
        <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-500/20 text-green-400 border border-green-500/30" data-testid="open-ring">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
          </span>
          Open
        </span>
      );
    }

    // Market is closed — show context from validation result
    if (validationResult) {
      const { market_status, holiday_name, date } = validationResult;

      // Holiday
      if (holiday_name || market_status === 'holiday') {
        return (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30"
                title={holiday_name ? `Holiday: ${holiday_name}` : 'Market holiday'}>
            <Calendar size={10} />
            {holiday_name || 'Holiday'}
          </span>
        );
      }

      // Weekend
      if (market_status === 'weekend') {
        return (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
            <Calendar size={10} />
            Weekend
          </span>
        );
      }
    }

    // Regular closed — static grey dot, deliberately lifeless next to the pulse
    return (
      <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-dark-500/20 text-dark-300 border border-dark-500/30">
        <span className="inline-flex rounded-full h-2 w-2 bg-dark-400" />
        Closed
      </span>
    );
  };

  const getValidationBadges = (result, extraVerifiedSources = [], marketOpen = null) => {
    if (!result) return null;

    if (result.status === 'pending' || result.status === 'error') {
      // 'pending' with the market CLOSED: distinguish an actual source outage
      // from sources that simply haven't published this session's close yet.
      const info = sourcesDownInfo(result);
      if (marketOpen === false && info && info.down.length > 0) {
        return [(
          <span
            key="sources-down"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border cursor-help bg-amber-500/15 text-amber-400 border-amber-400/40"
            title={`Couldn't verify today's close — source(s) unreachable:\n${info.down.map(d => `${d.name}: ${d.error}`).join('\n')}\nData itself is unaffected (MarketVibes supplies it); verification resumes when a source recovers.`}
          >
            <AlertCircle size={10} />
            Unverified (sources down)
          </span>
        )];
      }
      // Sources reachable but haven't published this session's close yet
      // (normal lag after any close), market open, or not validated yet —
      // all NORMAL states: say nothing. The Verified badge appears on its
      // own once sources publish; silence is the correct signal until then.
      return null;
    }

    // Settling — market just closed, too early to verify
    if (result.settling) {
      return [(
        <span
          key="settling"
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border cursor-help bg-amber-500/15 text-amber-400 border-amber-400/40 animate-pulse"
          title={result.note || 'Market closed less than 1 hour ago — close prices may still be settling'}
        >
          <Timer size={10} />
          Settling
        </span>
      )];
    }

    // Close Verified — at least one source confirms our close
    if (result.status === 'passed') {
      const sourceLabel = [...(result.verified_by || []), ...extraVerifiedSources].join(', ');
      const dateLabel = result.date ? formatShortDate(result.date) : '';
      return [(
        <span
          key="verified"
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border cursor-help bg-green-500/15 text-green-400 border-green-400/40"
          title={`Close price verified correct${dateLabel ? ` for ${dateLabel}` : ''}\nDB: ${result.db_close?.toFixed(2)}`}
        >
          <ShieldCheck size={10} />
          {dateLabel && <span className="text-green-400/70">{dateLabel}</span>}
          Verified{sourceLabel ? ` (${sourceLabel})` : ''}
        </span>
      )];
    }

    // Close Mismatch — no source confirms our close
    const mismatchDateLabel = result.date ? formatShortDate(result.date) : '';
    return [(
      <span
        key="mismatch"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border cursor-help bg-red-500/15 text-red-400 border-red-400/40"
        title={`Close price mismatch${mismatchDateLabel ? ` for ${mismatchDateLabel}` : ''} — does not match any external source\nDB: ${result.db_close?.toFixed(2)}`}
      >
        <XCircle size={10} />
        {mismatchDateLabel && <span className="text-red-400/70">{mismatchDateLabel}</span>}
        Mismatch
      </span>
    )];
  };

  // Independent NSE/BSE verdict chip for India indexes — provisional (aggregator)
  // during the day, then authoritative "Final · NSE/BSE" / mismatch / pending after 21:30 IST.
  const getIndiaVerifyBadge = (symbol) => {
    const v = indiaVerify[symbol] || (symbol && indiaVerify[symbol.toUpperCase()]);
    // Nothing before the 21:30 IST cutoff — the NSE/BSE verdict isn't due yet, so we
    // don't show a badge (and never a premature "pending"). Rendering is gated further
    // on the aggregator being verified (status 'passed') at the call site.
    // 'final_verified' is folded into the single "Verified (…sources…)" badge, so no
    // separate chip here. This helper only renders the exception states.
    if (!v || v.status === 'provisional' || v.status === 'final_verified') return null;
    const dayLabel = v.trading_day
      ? new Date(v.trading_day).toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short' })
      : '';
    if (v.status === 'mismatch') {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-500/20 text-red-300 border border-red-500/50"
          title={`Official ${v.exchange} close ${v.exchange_close} ≠ CL stored close ${v.cl_close} for ${dayLabel}.`}>
          <AlertCircle size={10} /> {v.exchange} mismatch
        </span>
      );
    }
    if (v.status === 'error') {
      // Proxy/exchange unreachable — we can't poll this exchange at all. Surface it distinctly.
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-orange-600/25 text-orange-300 border border-orange-500/50"
          title={`${v.exchange} fetch failed for ${dayLabel}${v.detail ? ` (${v.detail})` : ''} — ${v.exchange} unreachable via proxy, so this close could not be independently verified. Retrying.`}>
          <AlertCircle size={10} /> {v.exchange} fetch error
        </span>
      );
    }
    if (v.status === 'verifying' || v.status === 'unverified') {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/20 text-amber-300 border border-amber-400/50"
          title={`Not yet verified — awaiting the official ${v.exchange} close for ${dayLabel}. Fetching…`}>
          <Clock size={10} /> Awaiting {v.exchange}
        </span>
      );
    }
    return null;
  };

  const buildValidationTooltip = (result) => {
    if (!result) return '';
    const lines = [];
    if (result.date) lines.push(`Date: ${result.date}`);
    if (result.status === 'pending') {
      if (result.message) lines.push(result.message);
      if (result.retry_after_ist) lines.push(`Retry after: ${result.retry_after_ist} IST`);
      return lines.join('\n');
    }
    if (result.db_close != null) lines.push(`DB: ${result.db_close.toFixed(2)}`);
    if (result.sources) {
      for (const [src, info] of Object.entries(result.sources)) {
        const label = src.toUpperCase();
        if (info.close != null) {
          lines.push(`${label}: ${info.close.toFixed(2)} ${info.match ? '(match)' : '(MISMATCH)'}`);
        } else if (info.error) {
          lines.push(`${label}: ${info.error}`);
        } else {
          lines.push(`${label}: N/A`);
        }
      }
    }
    return lines.join('\n');
  };

  const getValidationForSymbol = (marketKey, symbolName) => {
    if (!validationData || !validationData[marketKey]) return null;
    return validationData[marketKey].find(v => v.symbol === symbolName);
  };

  const formatUpdateTime = (isoTimestamp) => {
    if (!isoTimestamp) return 'N/A';
    const dt = new Date(isoTimestamp);
    const dateStr = dt.toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
    });
    const timeStr = dt.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${timeStr}, ${dateStr} IST`;
  };

  const getMarketDisplayName = (marketKey) => {
    const marketNames = {
      'india_stocks_indexes': 'India',
      'us_stocks_indexes': 'US',
      'europe_stocks_indexes': 'Europe',
      'asia_pacific': 'Asia Pacific',
      'international_stocks_indexes': 'International',
      'crypto': 'Crypto',
      'precious_metals': 'Metals'
    };
    return marketNames[marketKey] || marketKey;
  };

  const getMarketOrder = () => {
    return ['india_stocks_indexes', 'us_stocks_indexes', 'europe_stocks_indexes', 'asia_pacific', 'crypto', 'precious_metals'];
  };

  const getOverallStatus = (data) => {
    let ready = 0, processing = 0, failed = 0, total = 0, marketsOpen = 0;

    // Only count markets that are displayed on the dashboard
    for (const key of marketOrder) {
      const market = data[key];
      if (!market) continue;
      market.forEach(item => {
        total++;
        if (item.status === 'ready') ready++;
        else if (item.status === 'processing') processing++;
        else if (item.status === 'failed') failed++;
        if (item.market_open === true) marketsOpen++;
      });
    }

    return { ready, processing, failed, total, marketsOpen };
  };

  const marketOrder = getMarketOrder();
  // remember this visit's markets and indexes for the next first-load skeleton
  const layoutSig = marketOrder.filter(k => pollingData[k]?.length).map(k => `${k}:${pollingData[k].map(i => i.symbol).join(',')}`).join('|');
  useEffect(() => {
    if (!layoutSig) return;
    try {
      localStorage.setItem(LAYOUT_KEY, JSON.stringify(layoutSig.split('|').map(g => { const [k, syms] = g.split(':'); return [k, syms.split(',')]; })));
    } catch { /* no storage: the skeleton just uses a generic shape */ }
  }, [layoutSig]);
  const overallStats = Object.keys(pollingData).length > 0 ? getOverallStatus(pollingData) : null;
  const validationSummary = validationData?.summary;

  // Group settling symbols by market and count totals
  const settlingByMarket = {};
  let settlingCount = 0;
  if (validationData) {
    for (const group of marketOrder) {
      const items = validationData[group];
      if (!items) continue;
      const settling = items.filter(item => item.settling).map(item => item.symbol);
      if (settling.length > 0) {
        settlingByMarket[group] = settling;
        settlingCount += settling.length;
      }
    }
  }

  // What is still on its way (Apal 2026-11-03: "each section that has a
  // skeleton and just gradually comes in" — "be clear about each section").
  // First load only: a Refresh keeps the old figures and narrates on its button.
  const pollingReady = !loading;
  const closeReady = !!validationData || validationFailed;
  const closePending = !closeReady;
  const sourcesPending = isAdmin() && !connectorHealth && !connectorFailed;
  const stillComing = [
    !pollingReady && 'index status',
    !diffReady && 'model checks',
    closePending && 'close checks',
    sourcesPending && 'data sources',
    plansLoading && 'action plans',
  ].filter(Boolean);
  const checksPending = [closePending && 'close prices', !diffReady && 'models', sourcesPending && 'data sources'].filter(Boolean);

  if (error) {
    return (
      <div className="bg-gradient-to-br from-dark-800 to-dark-900 rounded-xl p-6 border border-red-500/30 shadow-xl">
        <div className="flex items-center text-red-400">
          <XCircle size={20} />
          <span className="ml-3">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-dark-800 to-dark-900 rounded-xl p-6 border border-dark-600/50 shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Activity size={20} className="text-blue-400" />
            Index Status
          </h2>
          <p className="text-sm text-dark-300 mt-1">
            Data freshness and close-price checks per index
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Freshness is a standing fact, not something you click to learn */}
          {validationData?.validated_at && !validationLoading && (
            <span className="text-[11px] text-dark-500" title="When the checks on this page last ran">
              Checked {new Date(validationData.validated_at).toLocaleTimeString('en-GB', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit', hour12: true })} IST
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={validationLoading}
            aria-live="polite"
            className="flex items-center justify-center gap-1.5 px-3 py-1.5 min-w-[112px] border border-dark-600/60 hover:bg-dark-700 rounded-lg transition-colors disabled:opacity-60 text-xs text-dark-300"
            title="Refresh index status and close-price checks"
          >
            {justRefreshed ? (
              <>
                <CheckCircle2 size={14} className="text-emerald-400" />
                <span className="text-emerald-400">Updated</span>
              </>
            ) : (
              <>
                <BusyIcon busy={refreshing} icon={RefreshCw} className={`w-3.5 h-3.5 ${refreshing ? 'text-blue-400' : ''}`} />
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* first load: one strip naming what is still coming, with the seconds */}
      <ProcessingStatus active={!refreshing && stillComing.length > 0} title="Loading Today's Health"
        what={`still coming: ${stillComing.join(' · ')}`} className="mb-4" />

      {/* Issues bar — every red fact on this page as a chip; clicking one
          scrolls straight to it. The page grows with every index we add, so
          "is anything wrong?" must be answered up here, without scrolling.
          Quiet green line when nothing needs attention. */}
      {!overallStats && (
        <IssuesBar issues={[]} pending={['index status', ...checksPending]} onJump={jumpTo} className="mb-6" />
      )}
      {overallStats && (() => {
        const issues = [], reviewedIssues = [];
        for (const marketKey of marketOrder) {
          for (const it of (pollingData[marketKey] || [])) {
            const v = getValidationForSymbol(marketKey, it.symbol);
            const diff = shownDiff[it.symbol];
            const rv = reviewOf(reviews, it.symbol, diff);
            const sev = rowSeverity(it, v, diff, !!rv.mine);
            if (sev === 'red') {
              issues.push({ id: `row-${it.symbol}`, label: it.symbol, reason: redReason(it, v, diff, !!rv.mine) });
            } else if (sev === 'reviewed') {
              reviewedIssues.push({ id: `row-${it.symbol}`, label: it.symbol, reason: 'model risk change',
                title: `Dismissed by you on ${reviewDate(rv.mine.created_at)}: “${rv.mine.note}”` });
            }
          }
        }
        const PLAN_NAMES = { asia: 'Asia', crypto: 'Crypto', india: 'India', europe: 'Europe', us: 'US' };
        for (const p of todayPlans) {
          if (p.status === 'failed') {
            issues.push({ id: 'action-plans-strip', label: `${PLAN_NAMES[p.market] || p.market} plan`, reason: 'delivery failed' });
          }
        }
        if (isAdmin()) {
          const impact = connectorHealth?.verification_impact || {};
          for (const [key, imp] of Object.entries(impact)) {
            const line = impactLine(key, imp, connectorHealth?.connectors || {});
            if (line.severity === 'blind') {
              issues.push({ id: 'data-sources-strip', label: CONNECTOR_LABELS[key] || key, reason: 'close checks blind', title: line.text });
            }
          }
        }
        return <IssuesBar issues={issues} reviewed={reviewedIssues} onJump={jumpTo} pending={checksPending} className="mb-6" />;
      })()}

      {/* the three cards before the index status lands — each names its answer */}
      {!overallStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="kpi-skeleton">
          {['Indexes', 'Market data', 'Close checks'].map(label => <KpiSkeleton key={label} label={label} />)}
        </div>
      )}
      {/* Overall summary: three cards, each an ANSWER, not a status code.
          Green when nothing needs attention; red with the symbol names when
          something does — this page is where everyone comes to find issues. */}
      {overallStats && (() => {
        const dataIssues = [];
        for (const items of Object.values(pollingData)) {
          for (const it of (items || [])) {
            if (it.data_current === false || (it.status === 'failed' && it.data_current !== true)) {
              dataIssues.push(it.symbol);
            }
          }
        }
        // symbol -> is its market open right now (open-market pending is a
        // session in progress, NOT "awaiting sources")
        const openMap = {};
        for (const items of Object.values(pollingData)) {
          for (const it of (items || [])) openMap[it.symbol] = it.market_open;
        }
        const mismatches = [], awaitingNames = [];
        let inSession = 0;
        if (validationData) {
          for (const g of marketOrder) {
            for (const it of (validationData[g] || [])) {
              if (it.status === 'failed' && !it.settling) mismatches.push(it.symbol);
              else if (it.status === 'pending' && !it.settling) {
                if (openMap[it.symbol] === true) inSession += 1;
                else awaitingNames.push(it.symbol);
              }
            }
          }
        }
        const names = (arr) => arr.slice(0, 3).join(', ') + (arr.length > 3 ? ` +${arr.length - 3} more` : '');
        // The math must visibly add up: verified + mismatch + settling +
        // awaiting + in-session = all indexes — and every non-verified index
        // is NAMED (Apal had to ask "which 2 are unverified?"; never again).
        const closeParts = [];
        if (validationSummary) {
          if (mismatches.length > 0) closeParts.push(`Mismatch: ${names(mismatches)}`);
          if (settlingCount > 0) closeParts.push(`${settlingCount} settling`);
          if (awaitingNames.length > 0) closeParts.push(`Awaiting sources: ${names(awaitingNames)}`);
          if (inSession > 0) closeParts.push(`${inSession} in session`);
          if (validationSummary.error > 0) closeParts.push(`${validationSummary.error} check error`);
        }
        return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-dark-700/50 rounded-lg p-4 border border-dark-600/30 cb-fade-in"
            title="All active indexes across every market.">
            <div className="text-2xl font-bold text-white">{overallStats.total} index{overallStats.total === 1 ? '' : 'es'}</div>
            <div className="text-xs text-dark-400 mt-1">{overallStats.marketsOpen} market{overallStats.marketsOpen === 1 ? '' : 's'} open now</div>
          </div>
          <div className={`rounded-lg p-4 border cb-fade-in ${dataIssues.length === 0 ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/30'}`}
            title={dataIssues.length === 0
              ? 'Every index has all completed trading sessions stored (weekends and holidays accounted for) and its last update succeeded.'
              : `These indexes are missing a completed session or failed their last update: ${dataIssues.join(', ')}`}>
            <div className={`text-2xl font-bold ${dataIssues.length === 0 ? 'text-green-400' : 'text-red-400'}`}>
              {dataIssues.length === 0 ? '✓ Current' : `${dataIssues.length} behind`}
            </div>
            <div className={`text-xs mt-1 ${dataIssues.length === 0 ? 'text-green-400/70' : 'text-red-400'}`}>
              {dataIssues.length === 0 ? 'Market data — all sessions stored' : `Missing data: ${names(dataIssues)}`}
            </div>
          </div>
          {closePending ? <KpiSkeleton label="Close checks" testId="kpi-close-pending" /> : validationFailed && !validationData ? (
            // the check could not run: say so in grey — never a green "no mismatches"
            <div className="rounded-lg p-4 border bg-dark-700/50 border-dark-600/30 cb-fade-in" data-testid="kpi-close-failed"
              title="The close-price check could not be reached. Refresh to run it again.">
              <div className="text-2xl font-bold text-dark-300">Not checked</div>
              <div className="text-xs mt-1 text-dark-400">Close checks could not run · Refresh to retry</div>
            </div>
          ) : (
            <div className={`rounded-lg p-4 border cb-fade-in ${mismatches.length > 0 ? 'bg-red-500/10 border-red-500/30' : 'bg-green-500/10 border-green-500/20'}`}
              title="Stored closes are checked against independent sources (EODHD, Yahoo, NSE, BSE, MOEX, Coinbase) after each session settles. Every index is accounted for: verified + mismatch + settling + awaiting sources = total.">
              <div className={`text-2xl font-bold ${mismatches.length > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {validationSummary ? `${validationSummary.passed}/${validationSummary.total} verified` : '—'}
              </div>
              <div className={`text-xs mt-1 ${mismatches.length > 0 ? 'text-red-400' : 'text-green-400/70'}`}>
                {closeParts.length > 0 ? closeParts.join(' · ') : 'Close checks — no mismatches'}
              </div>
            </div>
          )}
        </div>
        );
      })()}

      {/* Action-plan notifications + data-source health, side by side */}
      <div className={`grid grid-cols-1 ${isAdmin() ? 'xl:grid-cols-2' : ''} gap-4 mb-6`}>
      {plansLoading && todayPlans.length === 0 && (
        // Reserve the strip's space while delivery status loads — the real
        // tiles replace these in place instead of a whole block popping in.
        <div className="bg-dark-700/30 rounded-lg p-4 border border-dark-600/20" data-testid="plans-skeleton">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider">Action Plans — Today</h3>
            <a href="/dashboard/notifications" className="text-xs text-primary-400 hover:text-primary-300 underline decoration-dotted">Full history →</a>
          </div>
          <div className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(110px,1fr))]">
            {[0, 1, 2, 3, 4].map(i => <TileSkeleton key={i} />)}
          </div>
        </div>
      )}
      {todayPlans.length > 0 && (
        <div id="action-plans-strip" className={`bg-dark-700/30 rounded-lg p-4 border border-dark-600/20${
          flashKey === 'action-plans-strip' ? ' ring-2 ring-red-400/80' : ''}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider">Action Plans — Today</h3>
            <a href="/dashboard/notifications" className="text-xs text-primary-400 hover:text-primary-300 underline decoration-dotted">Full history →</a>
          </div>
          <div className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(110px,1fr))] cb-fade-in">
            {todayPlans.map(p => (
              <div key={p.market}
                title={p.status === 'sent'
                  ? `Action plan delivered at ${new Date(p.at).toLocaleTimeString('en-GB', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit', hour12: true })} IST`
                  : p.status === 'failed' ? 'Action plan delivery failed — check the Notifications tab'
                  : 'Action plan not sent yet — goes out about 2 hours before this market opens'}
                className={`px-3 py-1.5 rounded-lg bg-dark-800/60 border cursor-help ${
                  p.status === 'sent' ? 'border-green-500/40' : p.status === 'failed' ? 'border-red-500/40' : 'border-dark-600'}`}>
                <div className="text-xs font-semibold text-white">{{ asia: 'Asia', crypto: 'Crypto', india: 'India', europe: 'Europe', us: 'US' }[p.market] || p.market}</div>
                {p.status === 'sent' && (
                  <div className="text-[11px] text-green-400">✓ sent {new Date(p.at).toLocaleTimeString('en-GB', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit', hour12: true })}</div>
                )}
                {p.status === 'failed' && <div className="text-[11px] text-red-400">✕ failed</div>}
                {p.status === 'pending' && <div className="text-[11px] text-dark-400">before open</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Data sources health — active connector self-tests; admins only */}
      {isAdmin() && (
        <div id="data-sources-strip" className={flashKey === 'data-sources-strip' ? 'ring-2 ring-red-400/80 rounded-lg' : ''}>
          <DataSourcesStrip health={connectorHealth} testing={connectorTesting} failed={connectorFailed} onTest={() => fetchConnectorHealth(true)} />
        </div>
      )}
      </div>

      {/* Market Groups. Scanning contract: a red-striped row = act, amber =
          watch, plain = fine; the group header pre-answers "any issues in
          here?" so healthy groups need no reading at all. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {!pollingReady && (Array.isArray(knownLayout) && knownLayout.length ? knownLayout : DEFAULT_LAYOUT).map(([marketKey, symbols]) => (
          <div key={marketKey} className="bg-dark-700/30 rounded-lg p-4 border border-dark-600/20" data-testid={`group-skeleton-${marketKey}`}>
            <h3 className="text-sm font-semibold text-blue-400 mb-3 uppercase tracking-wider flex items-center gap-2">
              {getMarketDisplayName(marketKey)}
              <Skel className="h-4 w-12" rounded="rounded-full" />
            </h3>
            <div className="space-y-2">
              {(symbols || []).map((sym, i) => <RowSkeleton key={sym || i} symbol={sym} />)}
            </div>
          </div>
        ))}
        {pollingReady && marketOrder.filter(marketKey => pollingData[marketKey] && pollingData[marketKey].length > 0).map((marketKey) => {
          const groupIssues = pollingData[marketKey].filter(it =>
            rowSeverity(it, getValidationForSymbol(marketKey, it.symbol), shownDiff[it.symbol],
              !!reviewOf(reviews, it.symbol, shownDiff[it.symbol]).mine) === 'red').length;
          return (
          <div key={marketKey} className="bg-dark-700/30 rounded-lg p-4 border border-dark-600/20 cb-fade-in">
            <h3 className="text-sm font-semibold text-blue-400 mb-3 uppercase tracking-wider flex items-center gap-2">
              {getMarketDisplayName(marketKey)}
              {/* the market's count waits for every check behind it */}
              {checksPending.length > 0 ? (
                <Skel className="h-4 w-12" rounded="rounded-full" testId={`group-pending-${marketKey}`} />
              ) : groupIssues > 0 ? (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold normal-case tracking-normal bg-red-500/20 text-red-400 border border-red-400/40" data-testid={`group-issues-${marketKey}`}>
                  {groupIssues} issue{groupIssues === 1 ? '' : 's'}
                </span>
              ) : (
                <span className="text-green-400/70 text-[10px] normal-case tracking-normal" data-testid={`group-ok-${marketKey}`}>✓</span>
              )}
            </h3>
            {settlingByMarket[marketKey] && (
              <div className="flex items-center gap-1.5 mb-3 px-3 py-1.5 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <AlertCircle size={12} className="text-yellow-400 flex-shrink-0" />
                <span className="text-[11px] text-yellow-300">
                  Settling — close prices may update within 1 hour of market close
                </span>
              </div>
            )}
            <div className="space-y-2">
              {pollingData[marketKey].map((item, index) => {
                const validationResult = getValidationForSymbol(marketKey, item.symbol);
                const lock = lockStatus[item.symbol];
                const diff = shownDiff[item.symbol];
                // When NSE/BSE independently confirms the close, fold it into the single
                // "Verified (EODHD, YAHOO, NSE)" source list instead of a separate badge.
                const indiaVerdict = indiaVerify[item.symbol] || indiaVerify[item.symbol.toUpperCase()];
                const indiaFinalSources = indiaVerdict?.status === 'final_verified' ? [indiaVerdict.exchange] : [];
                const review = reviewOf(reviews, item.symbol, diff);
                const severity = rowSeverity(item, validationResult, diff, !!review.mine);
                return (
                  <div
                    key={index}
                    id={`row-${item.symbol}`}
                    data-testid={`row-${item.symbol}`}
                    className={`p-3 rounded-lg hover:bg-dark-800 transition-colors ${
                      severity === 'red' ? 'bg-red-500/[0.06] border-l-2 border-red-500 rounded-l-none'
                        : severity === 'reviewed' ? 'bg-dark-800/50 border-l-2 border-dark-500 rounded-l-none' : 'bg-dark-800/50'}${
                      flashKey === `row-${item.symbol}` ? ' ring-2 ring-red-400/80' : ''}`}
                  >
                    {/* Row 1: Symbol, value, status (only if not ready), time ago */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(item)}
                        <span className="text-white font-medium text-sm">{item.symbol}</span>
                        {/* Freshness mark appears ONLY when a completed session is
                            genuinely missing (weekend/holiday-aware server-side —
                            Friday's data on a Sunday is current, no mark). Healthy
                            rows stay clean; the status circle already says OK. */}
                        {item.data_current === false && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-500/15 text-red-400 border border-red-400/40 cursor-help" data-testid={`fresh-${item.symbol}`}
                            title={`Missing data: expected through ${item.expected_through} (last completed session, holidays accounted for) but the latest stored session is ${item.data_through || 'none'}.`}>
                            ✗ data missing
                          </span>
                        )}
                        {item.model_revision != null && (
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${VERSION_CHIP[item.model_revision] || 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-400/40'}`}
                            title={`MarketVibes model v${item.model_revision}${item.model_revision_since ? ` — live since ${item.model_revision_since}` : ''}`}
                          >
                            v{item.model_revision}
                          </span>
                        )}
                        {!lockLoaded && <Skel className="h-[18px] w-14" rounded="rounded-full" testId={`lock-pending-${item.symbol}`} />}
                        {lock?.locked === true && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-sky-500/15 text-sky-300 border border-sky-400/30"
                            title={`Locked on MarketVibes — history is immutable, transactions won't change.${lock.model_version ? `\nModel: ${lock.model_version}` : ''}`}
                          >
                            <Lock size={9} />
                            Locked
                          </span>
                        )}
                        {lock?.locked === false && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/20 text-amber-300 border border-amber-400/50 shadow-[0_0_0_1px_rgba(251,191,36,0.15)]"
                            title={`Unlocked on MarketVibes — history can still be recomputed, so its transactions may change on a future reset.${lock.model_version ? `\nModel: ${lock.model_version}` : ''}`}
                          >
                            <Unlock size={9} />
                            Unlocked
                          </span>
                        )}
                        {item.last_close != null && (
                          <span className="text-dark-300 text-sm font-mono">
                            {item.last_close.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        )}
                        {item.status !== 'ready' && (
                          item.status === 'failed' && item.data_current ? (
                            // The last poll ATTEMPT failed but the data itself is complete —
                            // calm amber, not a red alarm. Clears on the next successful poll.
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/15 text-amber-300 border border-amber-400/40 cursor-help"
                              title={`Data is complete through ${item.data_through}. A later poll attempt failed — likely a temporary network/MV hiccup; this clears on the next successful poll. Nothing is missing.`}
                            >
                              Poll failed · data current
                            </span>
                          ) : (
                            <span
                              className={`${getStatusBadge(item.status)} cursor-help`}
                              title={item.status === 'failed' && item.expected_through
                                ? `Data expected through ${item.expected_through} but the latest stored day is ${item.data_through || 'none'} — this session is genuinely missing.`
                                : undefined}
                            >
                              {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                            </span>
                          )
                        )}
                      </div>
                      <span className="text-[11px] text-dark-400 text-right">
                        {formatUpdateTime(item.last_updated_at)}
                      </span>
                    </div>
                    {/* Row 2: Close time, market open/closed, validation */}
                    <div className="flex items-center gap-3 mt-1.5 ml-6 text-[11px] text-dark-400">
                      {item.always_open ? (
                        <span className="flex items-center gap-1">
                          <Timer size={10} />
                          Daily close: <span className="text-dark-300">5:30 AM IST</span>
                        </span>
                      ) : item.market_close_ist && (
                        <span className="flex items-center gap-1">
                          <Timer size={10} />
                          Close: <span className="text-dark-300">{formatCloseTimeIST(item.market_close_ist)} IST</span>
                        </span>
                      )}
                      {item.always_open ? (
                        <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-500/20 text-green-400 border border-green-500/30" data-testid="open-ring">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
                          </span>
                          Open · 24×7
                        </span>
                      ) : item.market_open !== null && item.market_open !== undefined && (
                        <>
                          {item.market_close_ist && <span className="text-dark-600">|</span>}
                          {getMarketOpenBadge(item.market_open, validationResult)}
                        </>
                      )}
                      {item.last_report_at && (
                        <>
                          <span className="text-dark-600">|</span>
                          <span className="flex items-center gap-1">
                            Report: <span className="text-dark-300">{item.last_report_at}</span>
                          </span>
                        </>
                      )}
                      {validationResult && getValidationBadges(validationResult, [], item.market_open) ? (
                        <>
                          <span className="text-dark-600">|</span>
                          <span className="inline-flex items-center gap-3 cb-fade-in">
                            {getValidationBadges(validationResult, indiaFinalSources, item.market_open)}
                          </span>
                        </>
                      ) : (
                        // its close check is still running: the chip's place shimmers
                        !validationResult && (closePending || validationLoading) && (
                          <>
                            <span className="text-dark-600">|</span>
                            <span className="inline-flex items-center gap-1.5 text-[10px] text-dark-500" data-testid={`close-pending-${item.symbol}`}
                              title="Checking this close against independent sources…">
                              <ShieldCheck size={10} />
                              <Skel className="h-[18px] w-32" rounded="rounded-full" />
                            </span>
                          </>
                        )
                      )}
                      {validationResult?.status === 'passed' && getIndiaVerifyBadge(item.symbol) && (
                        <>
                          <span className="text-dark-600">|</span>
                          {getIndiaVerifyBadge(item.symbol)}
                        </>
                      )}
                    </div>
                    {/* Row: live model version + freshness of the Latest dataset (the blob of all
                        signals + history MV recomputes with the live model and pushes after close).
                        The model version is a fact about the model; the second half is ONLY about
                        data delivery — never say the "model" wasn't pushed. */}
                    {/* Multiple issues can fire at once (stale Latest + risk verdict +
                        downloads). Chips never break mid-sentence: each is nowrap, the
                        row wraps BETWEEN chips — stays organized however many appear. */}
                    {/* the model and its reporting data, then what the Latest model
                        changes (Apal 2026-11-02: "needs to be more organized") */}
                    <div className="mt-1.5 ml-6 text-[11px] space-y-1">
                    {/* Apal 2026-11-03: two facts — the model (a code change) and the
                        reporting data (MarketVibes' run with a model) — each with its
                        version and when it was last updated */}
                    <ModelLines item={item} />
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 empty:hidden">
                      {/* Model-change verdict lives ON the model row it describes —
                          brief, risk-first; periods/years + metric table in the tooltip.
                          While the diff is still computing, hold its slot with a pulse
                          instead of letting the verdict pop in later. */}
                      {!diffReady && (
                        <span className="inline-flex items-center gap-1.5 text-dark-500" data-testid={`diff-pending-${item.symbol}`} title="Checking what the Latest model changes…">
                          <Skel className="h-3 w-56" />
                        </span>
                      )}
                      {/* the live model changed since MV's last Latest push: nothing new to
                          judge — say so, never a verdict (Apal 2026-11-02) */}
                      {diff?.latest_behind && (
                        <span data-testid={`latest-behind-${item.symbol}`} className="whitespace-nowrap text-dark-300"
                          title={`MarketVibes' last Latest push was computed while model v${diff.model_revision} was live; the live model is now v${diff.published_revision}. Judging it would grade the old model, so nothing is judged until a fresh Latest arrives.`}>
                          Latest is v{diff.model_revision}, live is v{diff.published_revision} — not judged until a fresh push
                        </span>
                      )}
                      {diff?.latest_available && diff.changed && (() => {
                        const imp = diff.impact;
                        const metricsTip = imp?.published && imp?.latest
                          ? `\nPublished → Latest:  CAGR ${imp.published.strategy_cagr}% → ${imp.latest.strategy_cagr}%  ·  worst loss ${imp.published.worst_loss}% → ${imp.latest.worst_loss}%  ·  cons. losses ${imp.published.worst_streak}% → ${imp.latest.worst_streak}%  ·  win rate ${imp.published.win_rate}% → ${imp.latest.win_rate}%`
                          : '';
                        const periodsTip = diff.episodes > 0 && diff.episode_years?.length
                          ? `\nRevised in ${diff.episodes} period(s): ${diff.episode_years.join(', ')}.`
                          : '';
                        const tip = `The current (Latest) model revises ${diff.trades_changed} past trade(s).${periodsTip}${metricsTip}`;
                        const openRisk = () => setDismissPopup({ symbol: item.symbol, fingerprint: imp.fingerprint, risks: imp.notes, metrics: imp.metrics,
                          version: diff.model_revision ?? item.model_revision, ...(review.mine ? { mode: 'view' } : { mode: 'dismiss', note: '' }) });
                        // the warning and its Dismiss travel together: on the scorecard's
                        // header when there is one (Apal 2026-10-30: "dismiss button is
                        // something I need to find"), inline otherwise
                        const card = !!(imp?.material && imp.metrics?.length > 0);
                        // dismissed by you: folded to one line unless opened again
                        const folded = card && !!review.mine && !openedDismissed.has(item.symbol);
                        const foldToggle = card && review.mine && (
                          <button onClick={() => toggleDismissed(item.symbol)} data-testid={`risk-fold-${item.symbol}`}
                            aria-expanded={!folded}
                            className="whitespace-nowrap inline-flex items-center gap-0.5 text-dark-400 hover:text-white transition-colors">
                            {folded ? <ChevronRight size={13} aria-hidden /> : <ChevronDown size={13} aria-hidden />}
                            {folded ? 'Show figures' : 'Hide'}
                          </button>
                        );
                        const verdict = imp && imp.material && (
                          <button onClick={openRisk} data-testid={`risk-open-${item.symbol}`} title={tip}
                            className={`whitespace-nowrap hover:text-white ${review.mine ? 'text-dark-400 font-semibold' : 'text-red-400 font-bold'}`}>
                            {review.mine ? 'Risk changed' : '⚠ Risk changed'}
                          </button>
                        );
                        // the warning is still THERE, just dismissed by you: one quiet
                        // indicator; the comment and "Bring warning back" live in the popup
                        const action = imp && imp.material && (review.mine ? (
                          <button onClick={openRisk}
                            data-testid={`dismissed-${item.symbol}`} title="Dismissed by you — click to see your comment or bring the warning back"
                            className="whitespace-nowrap inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-dark-700 border border-dark-600 text-xs font-medium text-dark-300 hover:bg-dark-600 hover:text-white transition-colors">
                            <span className="text-amber-400/70">⚠</span> Dismissed
                          </button>
                        ) : imp.fingerprint ? (
                          <button onClick={openRisk}
                            data-testid={`dismiss-${item.symbol}`}
                            title={review.others.length ? `Dismissed by ${review.others.map(o => o.username).join(', ')} — see their comment` : 'Dismiss this warning with a comment'}
                            className="whitespace-nowrap inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-dark-700 border border-dark-500 text-xs font-medium text-dark-100 shadow-sm hover:bg-dark-600 hover:text-white transition-colors">
                            <BellOff size={13} aria-hidden /> Dismiss
                          </button>
                        ) : null);
                        return (
                          <>
                            {imp && !imp.material && (
                              <span className="whitespace-nowrap text-emerald-400 font-semibold" title={tip}>
                                ✓ {imp.improvements?.length ? `Improved: ${imp.improvements[0]}` : 'No added risk'} · {diff.trades_changed} trade{diff.trades_changed === 1 ? '' : 's'} revised
                              </span>
                            )}
                            {(!card || folded) && verdict}
                            {folded && foldToggle}
                            {(!card || folded) && action}
                            {!imp && (
                              <span className="whitespace-nowrap text-fuchsia-300 font-semibold" title={tip}>{diff.trades_changed} trade{diff.trades_changed === 1 ? '' : 's'} revised</span>
                            )}
                            <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-dark-400" data-testid={`downloads-${item.symbol}`}>
                              <BusyIcon busy={downloading?.startsWith(`${item.symbol}:`)} icon={Download} className="w-2.5 h-2.5" />
                              <button
                                onClick={() => handleDownloadTransactions(item.symbol, 'published')}
                                disabled={!!downloading} aria-busy={downloading === `${item.symbol}:published`}
                                className="underline decoration-dotted hover:text-white transition-colors disabled:no-underline disabled:cursor-wait"
                                title="Download the Published (live) transactions for this index"
                              >
                                {downloading === `${item.symbol}:published` ? 'Downloading…' : 'Published'}
                              </button>
                              <span className="text-dark-600">·</span>
                              <button
                                onClick={() => handleDownloadTransactions(item.symbol, 'latest')}
                                disabled={!!downloading} aria-busy={downloading === `${item.symbol}:latest`}
                                className="underline decoration-dotted hover:text-white transition-colors disabled:no-underline disabled:cursor-wait"
                                title="Download the Latest (current-model) transactions for this index"
                              >
                                {downloading === `${item.symbol}:latest` ? 'Downloading…' : 'Latest'}
                              </button>
                            </span>
                            {/* critical, so every figure stays on the row — as an aligned scorecard
                                (same six columns on every row, fixed height) instead of wrapping text
                                (Apal 2026-10-30: "I don't like that clutter" / "show all issues right there") */}
                            {card && !folded && (
                              <div className={`basis-full mt-0.5 rounded-md border px-2.5 py-1.5 ${review.mine ? 'border-dark-700 bg-dark-900/30' : 'border-red-500/25 bg-red-500/[0.06]'}`}>
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <span className="inline-flex items-center gap-3">{verdict}{foldToggle}</span>
                                  {action}
                                </div>
                                <button onClick={openRisk} data-testid={`risk-${item.symbol}`}
                                  title="Published (live, what clients see) → Latest model — click for details"
                                  className={`w-full ${SCORECARD_COLS}`}>
                                  <RiskScorecard metrics={imp.metrics} version={diff.model_revision ?? item.model_revision} muted={!!review.mine} />
                                </button>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          );
        })}
      </div>
      <DismissPopup popup={dismissPopup}
        review={dismissPopup ? reviewOf(reviews, dismissPopup.symbol, modelDiff[dismissPopup.symbol]) : null}
        onChange={note => setDismissPopup(p => ({ ...p, note, error: null }))}
        onClose={() => setDismissPopup(null)}
        onDismiss={() => submitReview(dismissPopup.symbol, dismissPopup.fingerprint, 'dismissed', (dismissPopup.note || '').trim())}
        onRestore={() => submitReview(dismissPopup.symbol, dismissPopup.fingerprint, 'restored')} />
    </div>
  );
};

export default PollingStatusMonitor;
